import { ReactNode, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { Cross, DataEventProps, Row } from "@/libraries/wpl/foundations/src";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

export interface BannerAlertProps {
    id: string;
    text: string | ReactNode;
    linkUrl?: string;
    linkText?: string;
    dataEventProps?: DataEventProps;
}

const containerStyles = css({
    backgroundColor: wacToken("color.extended.blue100"),
    position: "relative",
    paddingInlineEnd: token("space.600"),
    [media.above.md]: {
        paddingInlineStart: token("space.600")
    },
    paddingBlock: token("space.150"),
    margin: "auto",
    display: "flex",
    zIndex: 1,
    [`&[data-dismissed="true"]`]: {
        display: "none"
    }
});

const paragraphStyles = css({
    margin: 0,
    [media.above.md]: {
        textAlign: "center"
    }
});

const textStyles = css({
    [media.above.sm]: {
        marginInlineEnd: token("space.100")
    }
});
const primaryInlineLinkStyles = css({
    // needed to have primary link styling but positioned inline (on tablet & desktop)
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    div: {
        display: "block",
        marginBlockStart: token("space.150"),
        [media.above.sm]: {
            display: "inline-block",
            marginBlockStart: 0
        }
    }
});

const closeIconStyles = css({
    appearance: "none",
    background: "transparent",
    border: 0,
    cursor: "pointer",
    padding: 0,
    position: "absolute",
    insetInlineEnd: "30px",
    insetBlockStart: token("space.200")
});

export const BannerAlert = ({
    id,
    text,
    linkText,
    linkUrl,
    dataEventProps
}: BannerAlertProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const bannerStorageItem = `BannerAlert_dismiss-${id}`;

    const closeLabel = intl.formatMessage({
        id: "banner-alert.close.aria-label",
        defaultMessage: "Close"
    });

    const [isHidden, setIsHidden] = useState<boolean>(true);

    useEffect(() => {
        let dismissValue = null;

        dismissValue = getLocalStorage(bannerStorageItem);

        if (dismissValue !== "1") {
            setIsHidden(false);
        }
    }, [bannerStorageItem]);

    return (
        <UFOSegment name="banner-alert">
            <div
                css={containerStyles}
                data-dismissed={isHidden ? "true" : undefined}
            >
                <button
                    aria-label={closeLabel}
                    css={closeIconStyles}
                    onClick={() => {
                        setIsHidden(true);
                        setLocalStorage(bannerStorageItem, "1");

                        createAnalyticsEvent({
                            eventType: "ui",
                            action: "clicked",
                            actionSubject: "linkButton",
                            attributes: {
                                page_path: location?.pathname,
                                url: location?.href,
                                label: closeLabel,
                                labelEnglish: "Close",
                                event: "clicked",
                                eventComponent: "linkButton",
                                eventContainer: "bannerAlertClose"
                            }
                        }).fire();
                    }}
                >
                    {/* @ts-ignore */}
                    <Cross size="20" color={wacToken("color.primary.black")} />
                </button>

                <Row layout="12">
                    <p css={paragraphStyles}>
                        <span css={textStyles}>{text}</span>
                        <span css={primaryInlineLinkStyles}>
                            {linkText && linkUrl && (
                                <Link
                                    aria-label={linkText}
                                    text={linkText}
                                    href={linkUrl}
                                    type="primary"
                                    dataEventProps={dataEventProps}
                                />
                            )}
                        </span>
                    </p>
                </Row>
            </div>
        </UFOSegment>
    );
};
